/**
 * UploadSection — drag-and-drop + camera-capture file uploader for
 * the booking flow. Customers attach reference photos / videos for
 * salon services so Rivky can prep before the appointment.
 *
 * Lifted state: parent owns the `value` array so it can ship the
 * URLs to the booking API on submit. The component handles upload
 * to /api/upload and feeds back results via onChange.
 *
 * Extracted from consultation/page.tsx — Phase 1.1 of booking
 * consolidation (wiki/plans/booking-consolidation-2026-04-28.md).
 * Behavior preserved verbatim: same accepted types, same 5-file
 * cap, same 10MB-per-file limit, same trackEvent name + payload.
 */
"use client";

import { useCallback, useRef, useState, type Dispatch, type SetStateAction } from "react";
import { Upload, Camera, Loader2, X } from "lucide-react";
import { useTranslations } from "next-intl";
import { useToast } from "@/context/ToastContext";
import { trackEvent } from "@/lib/track";
import { cn } from "@/lib/utils";

export interface UploadedFile {
  url: string;
  type: string;
  name: string;
  uploading: boolean;
}

const MAX_FILES = 5;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ACCEPTED_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/heic",
  "image/heif",
  "video/mp4",
  "video/quicktime",
];

interface UploadSectionProps {
  /** Lifted-state file list. Empty array = nothing uploaded yet. */
  value: UploadedFile[];
  /**
   * Setter (the React `useState` Dispatch). We use functional updates
   * internally so concurrent edits in the parent (e.g. user removes a
   * file while another is mid-upload) don't get clobbered.
   */
  setValue: Dispatch<SetStateAction<UploadedFile[]>>;
  /**
   * If false the section renders nothing (e.g. on a service type that
   * doesn't accept reference photos). Defaults to true so the simple
   * caller doesn't have to pass it.
   */
  enabled?: boolean;
  /**
   * i18n namespace to read keys from. Defaults to "consultation" so
   * existing translation files keep working unchanged. Phase 2 of the
   * consolidation will move keys to a shared "booking" namespace.
   */
  i18nNamespace?: string;
  /**
   * trackEvent name fired on a successful upload. Defaults match the
   * legacy /consultation event so analytics history isn't broken.
   */
  trackEventName?: string;
}

export default function UploadSection({
  value,
  setValue,
  enabled = true,
  i18nNamespace = "consultation",
  trackEventName = "consultation_file_uploaded",
}: UploadSectionProps) {
  const t = useTranslations(i18nNamespace);
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const uploadFile = useCallback(
    async (file: File) => {
      if (!ACCEPTED_TYPES.includes(file.type)) {
        toast(t("invalidFileType"), "error");
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        toast(
          t("fileTooLarge", { size: (file.size / 1024 / 1024).toFixed(1) }),
          "error"
        );
        return;
      }
      // Pre-flight max-files check. Earlier version did this inside a
      // setValue functional updater + closure flag, but React 18's
      // batched event-handler updates run the updater asynchronously,
      // so the flag was always false on the synchronous branch and the
      // toast fired on every upload (bug spotted in P2 smoke test).
      // Reading `value.length` here is fine — when the user is
      // selecting files one at a time it's never stale; concurrent
      // multi-file selects all see the same captured value and the
      // setValue functional update appends them all (we cap at MAX
      // server-side via the visible drag-zone hiding once value.length
      // hits MAX_FILES).
      if (value.length >= MAX_FILES) {
        toast(t("maxFilesAllowed", { max: MAX_FILES }), "error");
        return;
      }
      // Identify the row by a stable id rather than array index so a
      // simultaneous remove of another file mid-upload doesn't end up
      // stamping the URL onto the wrong row.
      const tempId = `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const tempEntry: UploadedFile & { _tempId?: string } = {
        url: "",
        type: file.type,
        name: file.name,
        uploading: true,
        _tempId: tempId,
      };
      setValue((prev) => [...prev, tempEntry]);

      try {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("folder", "service-attachments");

        const res = await fetch("/api/upload", {
          method: "POST",
          body: formData,
        });
        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.error || "Upload failed");
        }
        const data = await res.json();
        setValue((prev) =>
          prev.map((f) =>
            (f as UploadedFile & { _tempId?: string })._tempId === tempId
              ? { url: data.url, type: f.type, name: f.name, uploading: false }
              : f
          )
        );
        trackEvent(trackEventName, {
          fileType: file.type.startsWith("video/") ? "video" : "image",
          size: file.size,
        });
      } catch (err) {
        toast(err instanceof Error ? err.message : "Upload failed", "error");
        setValue((prev) =>
          prev.filter(
            (f) =>
              (f as UploadedFile & { _tempId?: string })._tempId !== tempId
          )
        );
      }
    },
    [value.length, setValue, t, toast, trackEventName]
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      files.forEach((file) => uploadFile(file));
      e.target.value = "";
    },
    [uploadFile]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      const files = Array.from(e.dataTransfer.files);
      files.forEach((file) => uploadFile(file));
    },
    [uploadFile]
  );

  const removeFile = useCallback(
    (idx: number) => {
      setValue((prev) => prev.filter((_, i) => i !== idx));
    },
    [setValue]
  );

  if (!enabled) return null;

  return (
    <div className="w-full">
      <label className="mb-1.5 block text-xs font-medium uppercase tracking-widest text-brand-muted">
        {t("uploadLabel")}
      </label>
      <p className="text-xs text-brand-muted/60 mb-3">{t("uploadHint")}</p>

      {/* Drag & drop zone */}
      {value.length < MAX_FILES && (
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          className={cn(
            "border-2 border-dashed rounded-xl p-8 text-center transition-colors duration-300 min-h-[120px]",
            dragOver
              ? "border-brand-gold bg-brand-gold/5"
              : "border-brand-dark/10 hover:border-brand-gold/50"
          )}
        >
          <Upload className="w-8 h-8 mx-auto mb-3 text-brand-muted" />
          <p className="text-sm text-brand-muted">
            {t("uploadDragText")}{" "}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="text-brand-gold underline"
            >
              {t("uploadBrowse")}
            </button>
          </p>
          <p className="text-xs text-brand-muted/60 mt-1">
            {t("uploadFormats")}
          </p>
          <input
            type="file"
            ref={fileInputRef}
            multiple
            accept="image/jpeg,image/png,image/webp,image/heic,image/heif,video/mp4,video/quicktime"
            className="hidden"
            onChange={handleFileSelect}
          />

          {/* Mobile camera button */}
          <button
            type="button"
            onClick={() => cameraInputRef.current?.click()}
            className="mt-4 inline-flex items-center gap-2 w-full sm:w-auto justify-center py-3 px-6 rounded-lg border border-brand-gold/30 text-sm text-brand-gold hover:bg-brand-gold/5 transition-colors sm:hidden"
          >
            <Camera className="h-4 w-4" />
            {t("uploadTakePhoto")}
          </button>
          <input
            type="file"
            ref={cameraInputRef}
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={handleFileSelect}
          />
        </div>
      )}

      {/* Thumbnail previews */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-3 mt-4">
          {value.map((file, i) => (
            <div key={i} className="relative group">
              {file.type.startsWith("video/") ? (
                <video
                  src={file.url || undefined}
                  className="w-20 h-20 object-cover rounded-lg bg-gray-100"
                />
              ) : (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={file.url || undefined}
                  alt={file.name}
                  className="w-20 h-20 object-cover rounded-lg bg-gray-100"
                />
              )}
              <button
                type="button"
                onClick={() => removeFile(i)}
                className="absolute -top-2 -end-2 bg-red-500 text-white rounded-full w-5 h-5 text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="h-3 w-3" />
              </button>
              {file.uploading && (
                <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center">
                  <Loader2 className="w-5 h-5 animate-spin text-white" />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {value.length > 0 && (
        <p className="text-xs text-brand-muted/60 mt-2">
          {t("uploadCount", { current: value.length, max: MAX_FILES })}
        </p>
      )}
    </div>
  );
}
